/**
 * VRG-716 — the COMMERCE PLATFORM a prospect's storefront runs on, as a closed set.
 *
 * An enum and not free text because Spencer wants ROI clustered by company attributes later
 * (vera-inc memory gtm/client_roi_clustering_future.md: "design deal fields as enums/FKs now so they
 * group later"). Free text would split one platform across "Shopify", "shopify", "Shopify Plus".
 *
 * Detected from pages the demo flow already fetches (apps/api demo/shared/detect-commerce-platform.ts)
 * and stored on the sales company (`sales_accounts.commerce_platform`, migration 0160), carried there
 * from the demo's `demo_provisioning_audit` row by the sales sync.
 *
 * `custom` and `unknown` are deliberately different answers:
 *   custom   — we READ the site and found no known platform's fingerprint (a bespoke build, or a
 *              platform not on this list).
 *   unknown  — we could not tell (the page did not load, or a person said so).
 * NULL (no value at all) means nobody has looked yet.
 */
export const COMMERCE_PLATFORMS = [
  'shopify',
  'woocommerce',
  'bigcommerce',
  'squarespace',
  'wix',
  'magento',
  'salesforce_commerce_cloud',
  'shopware',
  'prestashop',
  'webflow',
  'ecwid',
  'custom',
  'unknown',
] as const;
export type CommercePlatform = (typeof COMMERCE_PLATFORMS)[number];

export const isCommercePlatform = (v: unknown): v is CommercePlatform =>
  typeof v === 'string' && (COMMERCE_PLATFORMS as readonly string[]).includes(v);

export const COMMERCE_PLATFORM_LABELS: Readonly<Record<CommercePlatform, string>> = {
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  bigcommerce: 'BigCommerce',
  squarespace: 'Squarespace',
  wix: 'Wix',
  magento: 'Magento / Adobe Commerce',
  salesforce_commerce_cloud: 'Salesforce Commerce Cloud',
  shopware: 'Shopware',
  prestashop: 'PrestaShop',
  webflow: 'Webflow',
  ecwid: 'Ecwid',
  custom: 'Custom / other',
  unknown: 'Unknown',
};

/** "Shopify"; an empty string for no value, so a caller picks its own empty state. */
export const commercePlatformLabel = (p: CommercePlatform | null | undefined): string => (p ? COMMERCE_PLATFORM_LABELS[p] : '');

/**
 * Who set the value. A DETECTED value is replaced by a later detection; a MANUAL one (a person
 * picked it, on the Create demo modal or the Sales company form) is never overwritten by detection.
 */
export const COMMERCE_PLATFORM_SOURCES = ['detected', 'manual'] as const;
export type CommercePlatformSource = (typeof COMMERCE_PLATFORM_SOURCES)[number];
export const isCommercePlatformSource = (v: unknown): v is CommercePlatformSource =>
  typeof v === 'string' && (COMMERCE_PLATFORM_SOURCES as readonly string[]).includes(v);
